import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import {
  TOP_N,
  buildMatrix,
  fmtMD,
  heatBg,
  rollupTopN,
  toMD,
} from '../lib/reports.js';
import { Icon } from './Icon.jsx';
import { Select } from './Select.jsx';

// Reports — org-wide client analytics for super-approvers. Answers the
// reporting requirement "Time spent on client support tracked across all
// involved departments." Per month, with an optional department filter:
//   • Total client-attributed time (MD), client share, MoM delta
//   • By client   (top 10 + Others)
//   • By activity (top 10 + Others) — spans client + internal work, common
//     activities merged across depts
//   • Client × department matrix (top 10 clients + Others)
// Read-only: no approve/lock, no drill-in, no export. Every figure is in
// man-days (MD) to one decimal — hours never appear here.

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const labelForMonth = (mk) => {
  const [y, m] = String(mk).split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

// Per-breakdown palettes — distinct tones inside one hue family.
const REPORTS_PALETTE = {
  client: ['#2E6BE6', '#5D8EF5', '#1F4FB0', '#8FB4FF', '#3F77E8', '#C3D6FF'], // blue family
  activity: ['#00C1C1', '#05E6C5', '#007C7C', '#58EED8', '#0FA3A3', '#B2F7ED'], // mint/teal family
};
// FNV-1a 32-bit hash → stable per-key shade (so a row keeps its colour).
const hashRow = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};
const shadeFor = (palette, key, idx) =>
  palette[(hashRow(String(key)) + idx) % palette.length];

/* ── Month + department controls ───────────────────────────── */
function ReportsControls({ value, currentMonth, months, departments, department, onMonthChange, onDepartmentChange }) {
  const monthOpts = useMemo(
    () =>
      [...months].reverse().map((mk) => ({
        key: mk,
        label: labelForMonth(mk) + (mk === currentMonth ? ' · in progress' : ''),
      })),
    [months, currentMonth],
  );
  const deptOpts = [
    { key: 'all', label: 'All departments' },
    ...departments.map((d) => ({ key: d, label: d })),
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ width: 230 }}>
        <label className="tt-field-label" style={{ fontSize: 11, marginBottom: 6, display: 'block' }}>
          Month
        </label>
        <Select value={value} options={monthOpts} getKey={(o) => o.key} getLabel={(o) => o.label} onChange={onMonthChange} />
      </div>
      <div style={{ width: 200 }}>
        <label className="tt-field-label" style={{ fontSize: 11, marginBottom: 6, display: 'block' }}>
          Department
        </label>
        <Select value={department} options={deptOpts} getKey={(o) => o.key} getLabel={(o) => o.label} onChange={onDepartmentChange} />
      </div>
    </div>
  );
}

/* ── Hero stat block (right rail) ──────────────────────────── */
function RStat({ label, value, hint }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sp-fg-3)' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--sp-fg-1)', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--sp-fg-3)', marginTop: 1 }}>{hint}</div>}
    </div>
  );
}

/* Delta chip — vs the previous month. */
function DeltaChip({ pct, prevLabel }) {
  if (pct == null) {
    return <span style={{ fontSize: 12, color: 'var(--sp-fg-3)' }}>No prior month to compare</span>;
  }
  const up = pct >= 0;
  const flat = Math.abs(pct) < 0.5;
  const color = flat ? 'var(--sp-fg-3)' : up ? '#007C7C' : '#B57651';
  const bg = flat ? 'var(--sp-bg-muted)' : up ? 'var(--sp-mint-100)' : 'var(--sp-apricot-100)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--sp-fg-2)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, background: bg, color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {!flat && <span aria-hidden="true">{up ? '▲' : '▼'}</span>}
        {flat ? 'No change' : `${up ? '+' : ''}${pct.toFixed(0)}%`}
      </span>
      <span style={{ color: 'var(--sp-fg-3)' }}>vs {prevLabel}</span>
    </span>
  );
}

function ReportsHero({ agg, partial, delta }) {
  return (
    <div className="card" style={{ padding: '24px 28px', borderRadius: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 28, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sp-fg-3)', marginBottom: 6 }}>
            Client-attributed time {agg.department !== 'all' ? `· ${agg.department}` : '· all departments'}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--sp-fg-1)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtMD(agg.total)}
            </span>
          </div>
          <div style={{ marginTop: 10 }}>
            {partial ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, background: 'var(--sp-bg-muted)', color: 'var(--sp-fg-2)', fontWeight: 700, fontSize: 12 }}>
                <Icon name="clock" size={11} /> In progress — comparison hidden
              </span>
            ) : (
              <DeltaChip pct={delta ? delta.pct : null} prevLabel={delta ? delta.prevLabel : ''} />
            )}
          </div>

          {/* Client share of all logged time */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--sp-fg-2)', fontWeight: 600 }}>Share of all logged time</span>
              <span style={{ fontSize: 12, color: 'var(--sp-fg-3)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(agg.clientShare * 100)}% client · {fmtMD(agg.internal)} internal
              </span>
            </div>
            <div style={{ height: 10, borderRadius: 999, background: 'var(--sp-bg-muted)', overflow: 'hidden', display: 'flex' }}>
              <div style={{ width: `${agg.clientShare > 0 ? Math.max(2, Math.min(100, agg.clientShare * 100)) : 0}%`, height: '100%', background: 'linear-gradient(90deg, #00C1C1, #05E6C5)', borderRadius: 999, transition: 'width .4s ease' }} />
            </div>
          </div>
        </div>

        <div style={{ borderLeft: '1px solid var(--sp-border)', paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <RStat label="Non-client work" value={fmtMD(agg.internal)} hint="internal / non-billable" />
          <RStat label="Clients" value={agg.clientCount} hint="with client time" />
          <RStat label="Departments" value={agg.deptCount} hint="contributing" />
          <RStat label="Activities" value={agg.activityCount} hint="client & internal" />
        </div>
      </div>
    </div>
  );
}

/* ── Ranked breakdown card (MD + share + bar), fixed top-N + "Others" ── */
function RankedBreakdown({ title, colLabel, icon, rows, total, palette, caption, emptyText = 'No client time in this period.' }) {
  const maxMins = rows.length ? rows[0].mins : 1;
  const COLS = '1fr 72px 52px';
  const { shown, others, othersMins } = rollupTopN(rows, TOP_N);

  const BarRow = ({ label, mins, color, muted }) => {
    const pct = total ? Math.round((mins / total) * 100) : 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'baseline' }}>
          <span style={{ minWidth: 0, fontSize: 13.5, fontWeight: muted ? 500 : 600, fontStyle: muted ? 'italic' : 'normal', color: muted ? 'var(--sp-fg-3)' : 'var(--sp-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
            {label}
          </span>
          <span style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 700, color: muted ? 'var(--sp-fg-2)' : 'var(--sp-fg-1)', fontVariantNumeric: 'tabular-nums' }}>
            {toMD(mins).toFixed(1)}
          </span>
          <span style={{ textAlign: 'right', fontSize: 11.5, color: 'var(--sp-fg-3)', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: 'var(--sp-bg-muted)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.max(2, Math.round((mins / maxMins) * 100))}%`, height: '100%', background: muted ? 'var(--sp-fg-4)' : color, borderRadius: 999, transition: 'width .3s ease' }} />
        </div>
      </div>
    );
  };

  return (
    <div className="card" style={{ padding: '18px 22px 18px', borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
          <Icon name={icon} size={14} />
        </span>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--sp-fg-1)' }}>{title}</h3>
        {caption && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--sp-fg-3)' }}>{caption}</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sp-fg-4)', paddingBottom: 2, borderBottom: '1px solid var(--sp-border-soft)' }}>
        <span>{colLabel}</span>
        <span style={{ textAlign: 'right' }}>MD</span>
        <span style={{ textAlign: 'right' }}>Share</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: '20px 0', fontSize: 13, color: 'var(--sp-fg-3)', textAlign: 'center' }}>{emptyText}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {shown.map((r, i) => (
            <BarRow key={r.key} label={r.label} mins={r.mins} color={shadeFor(palette, r.key, i)} />
          ))}
          {others.length > 0 && <BarRow label={`Others (${others.length})`} mins={othersMins} muted />}
        </div>
      )}
    </div>
  );
}

/* ── Client × Department matrix — fixed top-N clients + "Others" ── */
function ClientDeptMatrix({ matrix, grandTotal }) {
  const { depts } = matrix;
  const allClients = matrix.clients; // sorted desc by total
  const visible = allClients.slice(0, TOP_N);
  const hidden = allClients.slice(TOP_N);
  const hasOthers = hidden.length > 0;
  const othersDeptMins = (d) => hidden.reduce((s, c) => s + matrix.get(c, d), 0);
  const othersTotal = hidden.reduce((s, c) => s + matrix.clientTotal(c), 0);
  const cols = `minmax(150px, 1.6fr) repeat(${depts.length}, minmax(0, 1fr)) 92px`;

  const Cell = ({ children, style }) => (
    <div style={{ padding: '11px 10px', fontVariantNumeric: 'tabular-nums', ...style }}>{children}</div>
  );

  return (
    <div className="card" style={{ padding: 0, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--sp-border)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
          <Icon name="users" size={14} />
        </span>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--sp-fg-1)' }}>
          Client × department{allClients.length > TOP_N ? ` (top ${TOP_N})` : ''}
        </h3>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--sp-fg-3)' }}>MD · shading = intensity</span>
      </div>

      {/* header row */}
      <div style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'stretch', borderBottom: '1px solid var(--sp-border)', background: 'var(--sp-bg-page)' }}>
        <Cell style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--sp-fg-3)', alignSelf: 'center' }}>Client</Cell>
        {depts.map((d) => (
          <Cell key={d} style={{ fontSize: 11, fontWeight: 700, color: 'var(--sp-fg-2)', textAlign: 'right', lineHeight: 1.25, alignSelf: 'center' }}>{d}</Cell>
        ))}
        <Cell style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--sp-fg-3)', textAlign: 'right', alignSelf: 'center' }}>Total</Cell>
      </div>

      {/* body rows */}
      {visible.map((c) => (
        <div key={c} style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'stretch', borderBottom: '1px solid var(--sp-border-soft)' }}>
          <Cell style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sp-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center' }}>{c}</Cell>
          {depts.map((d) => {
            const mins = matrix.get(c, d);
            return (
              <div
                key={d}
                title={mins > 0 ? `${c} · ${d} — ${fmtMD(mins)}` : `${c} · ${d} — none`}
                style={{ padding: '11px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', background: heatBg(mins, matrix.maxCell), fontSize: 13, fontWeight: mins > 0 ? 600 : 400, color: mins > 0 ? 'var(--sp-fg-1)' : 'var(--sp-fg-4)' }}
              >
                {mins > 0 ? toMD(mins).toFixed(1) : '·'}
              </div>
            );
          })}
          <Cell style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 700, color: 'var(--sp-fg-1)', alignSelf: 'center' }}>{toMD(matrix.clientTotal(c)).toFixed(1)}</Cell>
        </div>
      ))}

      {/* Others roll-up (long tail) */}
      {hasOthers && (
        <div style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'stretch', borderBottom: '1px solid var(--sp-border-soft)', background: 'var(--sp-bg-page)' }}>
          <Cell style={{ fontSize: 13, fontWeight: 600, fontStyle: 'italic', color: 'var(--sp-fg-3)', alignSelf: 'center' }}>Others ({hidden.length})</Cell>
          {depts.map((d) => (
            <Cell key={d} style={{ textAlign: 'right', fontSize: 13, fontWeight: 500, color: 'var(--sp-fg-3)' }}>{othersDeptMins(d) > 0 ? toMD(othersDeptMins(d)).toFixed(1) : '·'}</Cell>
          ))}
          <Cell style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 700, color: 'var(--sp-fg-2)', alignSelf: 'center' }}>{toMD(othersTotal).toFixed(1)}</Cell>
        </div>
      )}

      {/* totals row — always the full month (all clients), not just top N */}
      <div style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'stretch', background: 'var(--sp-bg-page)', borderTop: '1px solid var(--sp-border)' }}>
        <Cell style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--sp-fg-3)', alignSelf: 'center' }}>Total</Cell>
        {depts.map((d) => (
          <Cell key={d} style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--sp-fg-1)' }}>{toMD(matrix.deptTotal(d)).toFixed(1)}</Cell>
        ))}
        <Cell style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 800, color: 'var(--sp-fg-1)' }}>{toMD(grandTotal).toFixed(1)}</Cell>
      </div>
    </div>
  );
}

/* ── Top-level Reports view ────────────────────────────────── */
export function ReportsView() {
  // `month` is null until the first response lands; the server then defaults
  // to the last complete month and echoes it back, which the picker adopts.
  const [month, setMonth] = useState(null);
  const [department, setDepartment] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .reportsClientAnalytics(month, department)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.code === 'UNAUTH') return; // global session-expiry handler owns this
        setError('Could not load the client analytics report.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, department]);

  const matrix = useMemo(() => (data ? buildMatrix(data.agg) : null), [data]);
  // Keep the picker stable across department changes: show the picked month,
  // else the month the server resolved.
  const selectedMonth = month || data?.month || '';
  const periodLabel = data?.monthLabel || (selectedMonth ? labelForMonth(selectedMonth) : '');

  return (
    <div data-screen-label="Reports" style={{ flex: 1, overflowY: 'auto', background: 'var(--sp-bg-page)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 56px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sp-fg-3)', marginBottom: 4 }}>
              Client analytics
            </div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--sp-fg-1)' }}>
              {periodLabel || '—'}
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--sp-fg-2)', maxWidth: 560, lineHeight: 1.5 }}>
              Time spent on client support across all departments. Headline figures
              are client-attributed; the activity breakdown also counts non-client work.
            </p>
          </div>
          {data && (
            <ReportsControls
              value={selectedMonth}
              currentMonth={data.currentMonth}
              months={data.months}
              departments={data.departments}
              department={department}
              onMonthChange={setMonth}
              onDepartmentChange={setDepartment}
            />
          )}
        </div>

        {error && (
          <div className="tt-error-msg" style={{ marginBottom: 16 }}>
            <Icon name="alert" size={12} /> {error}
          </div>
        )}

        {!data && loading ? (
          <div style={{ padding: '64px 0', textAlign: 'center', color: 'var(--sp-fg-3)' }}>Loading…</div>
        ) : data ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <ReportsHero agg={data.agg} partial={data.partial} delta={data.delta} />

            {/* The two headline breakdowns */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              <RankedBreakdown title={`By client (top ${TOP_N})`} colLabel="Client" icon="user" rows={data.agg.byClient} total={data.agg.total} palette={REPORTS_PALETTE.client} />
              <RankedBreakdown title={`By activity (top ${TOP_N})`} colLabel="Activity" icon="flow" rows={data.agg.byActivity} total={data.agg.total + data.agg.internal} palette={REPORTS_PALETTE.activity} caption="all work · incl. non-client" emptyText="No time logged in this period." />
            </div>

            {/* Cross-tab — only meaningful with more than one department in scope */}
            {matrix && matrix.depts.length > 1 && (
              <ClientDeptMatrix matrix={matrix} grandTotal={data.agg.total} />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
